import { useEffect, useState } from "react";
import { BeadInfinity } from "./EthosBeads";

export default function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState("in"); // in → hold → out

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("hold"), 800);   // entrance settled
    const t2 = setTimeout(() => setPhase("out"),  5200);  // begin soft dissolve — ~4.4s ambient hold
    const t3 = setTimeout(() => onDone?.(),        6300); // app revealed — ~6.3s total
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div
      className="splash-root"
      aria-hidden="true"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: "linear-gradient(160deg, #1A0E2E 0%, #2D1B4E 40%, #1A1226 80%, #0E0B1A 100%)",
        opacity: phase === "out" ? 0 : phase === "in" ? 0 : 1,
        transition: phase === "out"
          ? "opacity 1.25s cubic-bezier(0.4,0,0.2,1)"   // sanctuary dissolve
          : "opacity 0.55s cubic-bezier(0,0,0.2,1)",    // gentle entrance bloom
        pointerEvents: phase === "out" ? "none" : "all",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes splashFloat {
          0%,100% { transform: translateY(0px) scale(1); }
          50%      { transform: translateY(-13px) scale(1.04); }
        }
        @keyframes splashGlow {
          0%,100% { opacity: 0.30; transform: translate(-50%,-50%) scale(1); }
          50%      { opacity: 0.62; transform: translate(-50%,-50%) scale(1.16); }
        }
        @keyframes splashMandala {
          0%   { transform: translate(-50%,-50%) rotate(0deg) scale(1); opacity: 0.5; }
          50%  { transform: translate(-50%,-50%) rotate(180deg) scale(1.04); opacity: 0.75; }
          100% { transform: translate(-50%,-50%) rotate(360deg) scale(1); opacity: 0.5; }
        }
        @keyframes particleDrift {
          0%   { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
          10%  { opacity: 0.9; }
          78%  { opacity: 0.5; }
          100% { transform: translateY(-120px) translateX(var(--px, 20px)) scale(0.35); opacity: 0; }
        }
        @keyframes splashTwinkle {
          0%,100% { opacity: 0.15; transform: scale(0.7); }
          50%      { opacity: 0.8;  transform: scale(1); }
        }
        @keyframes splashReveal {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes splashBeam {
          0%   { left: -94px; opacity: 0; }
          14%  { opacity: 1; }
          86%  { opacity: 1; }
          100% { left: 248px; opacity: 0; }
        }
        .splash-logo    { animation: splashFloat 6.5s ease-in-out infinite; }
        .splash-glow    { animation: splashGlow 5.5s ease-in-out infinite; }
        .splash-mandala { animation: splashMandala 44s linear infinite; }
        .splash-particle{ animation: particleDrift var(--dur,6s) ease-in-out var(--delay,0s) infinite; }
        .splash-r1 { animation: splashReveal 1.1s ease-out 0.5s both; }
        .splash-r2 { animation: splashReveal 1.1s ease-out 0.9s both; }
        .splash-r3 { animation: splashReveal 1.1s ease-out 1.5s both; }
        .splash-r4 { animation: splashReveal 1.1s ease-out 2.0s both; }
        .splash-twinkle { animation: splashTwinkle 3.2s ease-in-out infinite; }
        .splash-beam {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 88px; height: 2px; border-radius: 2px;
          background: linear-gradient(90deg, rgba(220,200,255,0) 0%, rgba(196,163,224,0.45) 65%, rgba(235,225,255,0.95) 100%);
          animation: splashBeam 2.6s cubic-bezier(0.45,0,0.55,1) infinite;
        }
        .splash-beam::after {
          content: ""; position: absolute; right: -2px; top: 50%; transform: translateY(-50%);
          width: 7px; height: 7px; border-radius: 50%;
          background: radial-gradient(circle, #ffffff 0%, #DCC6FF 45%, rgba(196,163,224,0) 78%);
          box-shadow: 0 0 9px 2px rgba(214,196,255,0.9), 0 0 18px 5px rgba(150,100,220,0.5);
        }
        @media (prefers-reduced-motion: reduce) {
          .splash-logo, .splash-glow, .splash-mandala, .splash-particle,
          .splash-r1, .splash-r2, .splash-r3, .splash-r4, .splash-twinkle {
            animation: none !important;
          }
          .splash-particle { opacity: 0 !important; }
          .splash-beam { animation: none !important; left: 58%; }
        }
      `}</style>

      {/* Ambient particles / twinkles */}
      {[
        { x: -60, dur: 6.5, delay: 0,    size: 3, opacity: 0.5 },
        { x:  40, dur: 7.2, delay: 0.9,  size: 2, opacity: 0.4 },
        { x: -20, dur: 5.8, delay: 1.7,  size: 4, opacity: 0.3 },
        { x:  70, dur: 7.8, delay: 0.4,  size: 2, opacity: 0.5 },
        { x: -80, dur: 6.8, delay: 2.2,  size: 3, opacity: 0.35 },
        { x:  30, dur: 6.2, delay: 2.8,  size: 2, opacity: 0.45 },
        { x: -40, dur: 7.5, delay: 1.1,  size: 3, opacity: 0.3 },
        { x:  55, dur: 5.9, delay: 2.0,  size: 2, opacity: 0.4 },
      ].map((p, i) => (
        <div
          key={i}
          className="splash-particle"
          style={{
            position: "absolute",
            bottom: `${20 + (i % 4) * 12}%`,
            left: `${30 + i * 7}%`,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: "#C4A3E0",
            opacity: p.opacity,
            "--px": `${p.x}px`,
            "--dur": `${p.dur}s`,
            "--delay": `${p.delay}s`,
          }}
        />
      ))}

      {/* Logo — ETHOS bracelet seal with mandala/lotus halo behind it */}
      <div className="splash-logo" style={{ position: "relative", zIndex: 2, marginBottom: 18 }}>
        {/* Mandala / lotus geometry — sacred halo behind the bead */}
        <svg
          className="splash-mandala"
          width="360" height="360" viewBox="0 0 360 360"
          style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)", zIndex: -2,
            pointerEvents: "none",
          }}
        >
          <g fill="none" stroke="#C4A3E0" strokeWidth="0.8" opacity="0.5">
            <circle cx="180" cy="180" r="150" />
            <circle cx="180" cy="180" r="120" strokeOpacity="0.6" />
            <circle cx="180" cy="180" r="92"  strokeOpacity="0.4" />
            {Array.from({ length: 12 }).map((_, i) => {
              return (
                <path
                  key={i}
                  d="M180 60 C 200 110, 200 150, 180 180 C 160 150, 160 110, 180 60 Z"
                  transform={`rotate(${i * 30} 180 180)`}
                  strokeOpacity="0.35"
                />
              );
            })}
          </g>
        </svg>

        {/* Soft plum aura for depth */}
        <div className="splash-glow" style={{
          position: "absolute", top: "50%", left: "50%",
          width: 420, height: 300, borderRadius: "50%",
          background: "radial-gradient(ellipse at center, rgba(116,72,200,0.40) 0%, rgba(60,34,110,0.18) 46%, transparent 72%)",
          filter: "blur(16px)", pointerEvents: "none", zIndex: -1,
        }} />

        <BeadInfinity size={188} variant="dark" animate="login" />
      </div>

      {/* Wordmark — primary ETHOS identity */}
      <p className="splash-logo-wordmark splash-r1" style={{
        fontFamily: "var(--font-heading, 'Playfair Display', Georgia, serif)",
        fontSize: 38, fontWeight: 700, letterSpacing: "0.30em",
        color: "rgba(232,216,250,0.98)", marginBottom: 12, paddingLeft: "0.30em",
        textTransform: "uppercase",
      }}>
        ETHOS
      </p>

      {/* Tagline */}
      <p className="splash-r2" style={{
        fontSize: 11.5, letterSpacing: "0.22em",
        color: "rgba(196,170,226,0.82)", fontWeight: 400, margin: 0,
        textTransform: "uppercase",
      }}>
        Values-first sanctuary network
      </p>

      {/* Divider twinkle star */}
      <div className="splash-twinkle splash-r3" style={{ margin: "30px 0 26px" }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 0 L8.2 5.8 L14 7 L8.2 8.2 L7 14 L5.8 8.2 L0 7 L5.8 5.8 Z" fill="#D8C6FF" opacity="0.9" />
        </svg>
      </div>

      {/* Loading group */}
      <div className="splash-r3" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <p style={{
          fontSize: 11, letterSpacing: "0.26em",
          color: "rgba(176,150,212,0.82)", fontWeight: 400,
          textTransform: "uppercase", marginBottom: 16,
        }}>
          Entering your sanctuary
        </p>

        {/* Thin lavender loading line with traveling glow-head */}
        <div style={{
          position: "relative", width: 240, height: 1, borderRadius: 1, overflow: "visible",
          background: "linear-gradient(90deg, rgba(196,163,224,0) 0%, rgba(196,163,224,0.32) 22%, rgba(196,163,224,0.32) 78%, rgba(196,163,224,0) 100%)",
        }}>
          <span className="splash-beam" />
        </div>
      </div>

      {/* Lower lotus + closing line */}
      <div className="splash-r4" style={{
        position: "absolute", bottom: "8%",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
      }}>
        <svg width="34" height="20" viewBox="0 0 34 20" fill="none" stroke="#9B7BC4" strokeWidth="1" opacity="0.6">
          <path d="M17 18 C 17 10, 17 4, 17 2 C 17 4, 17 10, 17 18" />
          <path d="M17 18 C 12 12, 9 7, 9 3 C 13 6, 16 11, 17 18" />
          <path d="M17 18 C 22 12, 25 7, 25 3 C 21 6, 18 11, 17 18" />
          <path d="M17 18 C 8 14, 3 10, 1 6 C 7 8, 13 13, 17 18" />
          <path d="M17 18 C 26 14, 31 10, 33 6 C 27 8, 21 13, 17 18" />
        </svg>
        <p style={{
          fontSize: 10, letterSpacing: "0.24em",
          color: "rgba(150,128,182,0.6)", fontWeight: 400, margin: 0,
          textTransform: "uppercase",
        }}>
          A space for intentional connection
        </p>
      </div>
    </div>
  );
}
